import {ContentOwnershipTypeFilter} from '@Components/content-planner/content-planner.types';
import type {IconProps} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {isUserPremium} from '@Libraries/user.library';
import {assertNever} from '@Utils/assert-never.util';
import type {TeamsUpsellSource} from '@Libraries/teams-upsell.library';
import {openTeamsUpsellDialog} from '@Libraries/teams-upsell.library';

export interface ContentOwnershipFilterOption {
  filter: ContentOwnershipTypeFilter;
  isPremiumLocked: boolean;
}

const PREMIUM_CONTENT_OWNERSHIP_FILTER_OPTIONS: readonly ContentOwnershipFilterOption[] = [
  {filter: ContentOwnershipTypeFilter.ALL, isPremiumLocked: false},
  {filter: ContentOwnershipTypeFilter.TEAM, isPremiumLocked: false},
  {filter: ContentOwnershipTypeFilter.OWN, isPremiumLocked: false},
];

/** Teams is a premium feature, so a non-premium user only ever sees their own content; "Shared in team" is offered as an upsell. */
const NON_PREMIUM_CONTENT_OWNERSHIP_FILTER_OPTIONS: readonly ContentOwnershipFilterOption[] = [
  {filter: ContentOwnershipTypeFilter.OWN, isPremiumLocked: false},
  {filter: ContentOwnershipTypeFilter.TEAM, isPremiumLocked: true},
];

export const getContentOwnershipFilterOptions = (): readonly ContentOwnershipFilterOption[] => {
  return isUserPremium() ? PREMIUM_CONTENT_OWNERSHIP_FILTER_OPTIONS : NON_PREMIUM_CONTENT_OWNERSHIP_FILTER_OPTIONS;
};

export const getDefaultContentOwnershipFilter = (): ContentOwnershipTypeFilter => {
  return isUserPremium() ? ContentOwnershipTypeFilter.ALL : ContentOwnershipTypeFilter.OWN;
};

/** ALL and TEAM read the same on every surface; only the OWN label differs ("My content" vs "My lists"), so the surface names its key. */
export const getContentOwnershipFilterLabel = (filter: ContentOwnershipTypeFilter, ownLabelKey: string): string => {
  switch (filter) {
    case ContentOwnershipTypeFilter.ALL:
      return window.i18next.t('pmwjs_all');
    case ContentOwnershipTypeFilter.TEAM:
      return window.i18next.t('pmwjs_shared_in_team');
    case ContentOwnershipTypeFilter.OWN:
      return window.i18next.t(ownLabelKey);
    default:
      return assertNever(filter);
  }
};

export const getPremiumLockedCrownIconProps = (): IconProps => {
  return {icon: 'icon-crown', size: IconSize.SIZE_ICON_16, className: 'ct-premium-primary-icon'};
};

export const selectContentOwnershipFilterOption = (
  option: ContentOwnershipFilterOption,
  onFilterChange: (filter: ContentOwnershipTypeFilter) => void,
  upsellSource: TeamsUpsellSource
): void => {
  if (option.isPremiumLocked) {
    openTeamsUpsellDialog(upsellSource, {shouldOpenInNewTab: true});
    return;
  }

  onFilterChange(option.filter);
};
